import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(t.contact.messageSent);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: t.contact.phone, value: '+48 735 598 146', href: 'tel:+48735598146' },
    { icon: Phone, label: t.contact.phone + ' 2', value: '+48 690 284 078', href: 'tel:+48690284078' },
    { icon: Mail, label: t.contact.email, value: 'propoziom@gmail.com', href: 'mailto:propoziom@gmail.com' },
    { icon: MapPin, label: t.contact.address, value: 'Polska', href: null },
    { icon: Clock, label: t.contact.hours, value: 'Pn-Pt: 8:00-18:00\nSob: 9:00-14:00', href: null }
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <div className="bg-[#111111] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-yellow-500 mb-2 block">{t.contact.getInTouch}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none text-white" data-testid="contact-title">{t.contact.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-heading text-3xl uppercase text-white mb-8">{t.contact.hereToHelp}</h2>
            <p className="text-zinc-400 leading-relaxed mb-8">{t.contact.description}</p>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#111111] border border-zinc-800 flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-yellow-400" strokeWidth={2} />
                  </div>
                  <div>
                    <span className="font-mono text-xs tracking-widest uppercase text-zinc-500 block mb-1">{item.label}</span>
                    {item.href ? (
                      <a href={item.href} className="text-white hover:text-yellow-400 transition-colors whitespace-pre-line">{item.value}</a>
                    ) : (
                      <span className="text-white whitespace-pre-line">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-zinc-800 bg-[#111111] p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-500 mx-auto mb-6 flex items-center justify-center">
                  <Check size={32} className="text-white" />
                </div>
                <h3 className="font-heading text-2xl uppercase text-white mb-4">{t.contact.messageSent}</h3>
                <p className="text-zinc-400 mb-6">{t.contact.thankYouMessage}</p>
                <button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', message: '' }); }} className="text-yellow-400 font-mono text-sm tracking-widest uppercase hover:underline">
                  {t.contact.sendAnother}
                </button>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl uppercase text-white mb-6">{t.contact.sendMessage}</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.contact.yourName}</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" placeholder="Jan Kowalski" data-testid="contact-name" />
                  </div>

                  <div>
                    <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.contact.email}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full" placeholder="jan@example.com" data-testid="contact-email" />
                  </div>

                  <div>
                    <label className="font-mono text-xs tracking-widest uppercase text-zinc-400 mb-2 block">{t.contact.message}</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="bg-[#050505] border border-zinc-700 text-white px-4 py-3 font-sans focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400 transition-all w-full resize-none" placeholder={t.contact.howCanWeHelp} data-testid="contact-message" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full bg-yellow-400 text-black uppercase font-bold tracking-widest px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                    {loading ? (<><Loader2 size={20} className="animate-spin" /> {t.contact.sending}</>) : t.contact.sendMessage}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
